import type { ScriptResultItem } from "../../api/models/script-runner";
import type { RunStatusType } from "../../components/rules/RulesResultPanel";

/**
 * Pure, dependency-free per-row status aggregation for streaming rule runs.
 *
 * A results-panel row (one Rule) doesn't map 1:1 to a backend scriptId: TABLE_TOTALS does
 * (`TABLE_TOTALS_<ruleId>`), CONSISTENCY fans out to one scriptId per tag
 * (`CONSISTENCY_<ruleId>_<tagId>`), and CUSTOM uses a single scriptId (`CUSTOM_<ruleId>`) — see
 * `buildScriptId` in `RulesRunnerHelper.ts`, which this module only matches against, never
 * generates.
 *
 * Tracks, for one row, which of its expected scriptIds have resolved and its current
 * `RunStatusType`. A row only flips to a terminal status once every constituent scriptId has
 * resolved — a naive 1:1 reducer would mark a 3-tag CONSISTENCY row VERIFIED on its first result.
 *
 * Kept pure (type-only imports, no office/api/react/store runtime deps) so both the blocking and
 * streaming code paths share the same aggregation contract, and so this file is unit-testable in
 * isolation with zero mocking.
 */

/**
 * Backend `exceptionCode` signaling an aborted/canceled script execution — spec
 * `docs/IDS-7235-run-rules-streaming-results/05-data-model.md` §7 (EXECUTION_ABORTED).
 */
export const EXECUTION_ABORTED_EXCEPTION_CODE = 0x91;

/**
 * Per-result status mapper: EXECUTION_ABORTED -> CANCELED (checked first, so an aborted script is
 * never mislabeled ERROR), missing/ERROR status -> ERROR, non-SUCCESS -> FAILED, SUCCESS ->
 * VERIFIED. Mirrors `evaluateStatus` in `RulesRunnerHelper.ts` — keep the two in sync. Mirrored
 * rather than imported because that module is heavy (office/api/redux runtime deps) and this file
 * must stay pure.
 */
export function mapScriptResultStatus(result: ScriptResultItem): RunStatusType {
  if (result.exceptionCode === EXECUTION_ABORTED_EXCEPTION_CODE) return "CANCELED";
  if (!result.status || result.status.toUpperCase().includes("ERROR")) return "ERROR";
  if (result.status !== "SUCCESS") return "FAILED";
  return "VERIFIED";
}

/**
 * Aggregate precedence applied once every scriptId a row expects has resolved: any CANCELED
 * sibling wins outright, else any ERROR, else any FAILED, else VERIFIED. Mirrors `evaluateStatus`'s
 * own precedence (`RulesRunnerHelper.ts`) — a partially-aborted row (e.g. some tags aborted, others
 * failed) must surface as CANCELED, not ERROR/FAILED, so a canceling user sees "Canceled" rather
 * than an error to chase. Keep the two in sync; a cross-check suite in `runAggregation.test.ts`
 * guards this.
 */
const AGGREGATE_PRECEDENCE: readonly RunStatusType[] = ["CANCELED", "ERROR", "FAILED", "VERIFIED"];

function aggregateResolvedStatuses(resolved: ReadonlyMap<string, RunStatusType>): RunStatusType {
  const present = new Set(resolved.values());
  for (const status of AGGREGATE_PRECEDENCE) {
    if (present.has(status)) return status;
  }
  // Unreachable in practice — kept as a safe, non-throwing fallback.
  return "VERIFIED";
}

/**
 * Immutable per-row aggregation state. Treat `expected`/`resolved` as read-only — never mutate
 * either in place; `createRowAggregation`/`ingestScriptResult` always return a fresh state instead.
 */
export interface RowAggregationState {
  /** The full set of scriptIds this row expects to hear about, fixed at creation. */
  readonly expected: readonly string[];
  /** scriptId -> mapped status, for every scriptId that has resolved so far (streamed or early). */
  readonly resolved: ReadonlyMap<string, RunStatusType>;
  /** "RUNNING" until every entry in `expected` has a `resolved` entry, then the aggregate status. */
  readonly status: RunStatusType;
}

/**
 * Folds one `ScriptResultItem` into `resolved` if its scriptId is part of `expected`.
 *
 * A result whose scriptId is missing or not in `expected` is ignored (returns the same map
 * reference) — a defensive no-op rather than throwing, since that would be a caller routing bug,
 * not something this reducer should raise. A result for an already-resolved scriptId overwrites
 * the previous entry (last write wins).
 */
function foldResult(
  expected: readonly string[],
  resolved: ReadonlyMap<string, RunStatusType>,
  result: ScriptResultItem
): ReadonlyMap<string, RunStatusType> {
  if (!result.scriptId || !expected.includes(result.scriptId)) {
    return resolved;
  }
  const next = new Map(resolved);
  next.set(result.scriptId, mapScriptResultStatus(result));
  return next;
}

function finalize(expected: readonly string[], resolved: ReadonlyMap<string, RunStatusType>): RowAggregationState {
  const isFullyResolved = expected.length > 0 && expected.every((scriptId) => resolved.has(scriptId));
  const status: RunStatusType = isFullyResolved ? aggregateResolvedStatuses(resolved) : "RUNNING";
  return { expected, resolved, status };
}

/**
 * Creates a row's initial aggregation state, folding in any pre-resolved "early" results (e.g. a
 * CONSISTENCY tag validation error computed locally). A row whose entire expected set is already
 * satisfied by `earlyResults` resolves to its terminal status immediately, never `"RUNNING"`.
 */
export function createRowAggregation(
  expectedScriptIds: string[],
  earlyResults: ScriptResultItem[] = []
): RowAggregationState {
  const expected: readonly string[] = [...expectedScriptIds];
  const resolved = earlyResults.reduce<ReadonlyMap<string, RunStatusType>>(
    (acc, earlyResult) => foldResult(expected, acc, earlyResult),
    new Map()
  );
  return finalize(expected, resolved);
}

/**
 * Ingests one streamed `script-result` into an existing row state and returns a new
 * `RowAggregationState` — `state` is never mutated, and is returned as-is when the result is an
 * ignored unknown/missing scriptId. The row stays RUNNING until every expected scriptId has
 * resolved, then the aggregate status is computed.
 */
export function ingestScriptResult(state: RowAggregationState, result: ScriptResultItem): RowAggregationState {
  const resolved = foldResult(state.expected, state.resolved, result);
  if (resolved === state.resolved) {
    return state;
  }
  return finalize(state.expected, resolved);
}
